import { createMainAppMicroserviceInstance } from '@servicetitan/cp-api';
import { inject, injectable, optional } from '@servicetitan/react-ioc';
import { AxiosInstance, AxiosPromise, AxiosRequestConfig, CancelToken } from 'axios';
import { makeCodedException } from 'utils';

export interface AddressModel {
    street?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    zip?: string | undefined;
    unit?: string | undefined;
}

export interface LocationModel {
    id: number;
    name: string;
    address?: AddressModel | undefined;
}

export interface CustomerLocationsModel {
    id: number;
    name: string;
    locations: LocationModel[];
}

/**
 * Thin wrapper for the existing monolith `service-addresses` endpoint
 * (`app` repo: CustomerPortalCustomersController.GetServiceAddressesAsync),
 * mirroring the shape of the hand-vendored dashboard client
 * (`modules/dashboard/api/portal-customers.api.ts`) without cross-importing it.
 */
@injectable()
class CustomerPortalCustomers {
    constructor(
        private readonly axios: AxiosInstance,
        private readonly baseUrl = ''
    ) {}

    getServiceAddresses(cancelToken?: CancelToken): AxiosPromise<CustomerLocationsModel[]> {
        const options = <AxiosRequestConfig>{
            baseURL: this.baseUrl,
            cancelToken,
            url: '/v1/customerportal2/customers/service-addresses',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        };

        return makeCodedException(this.axios.request<CustomerLocationsModel[]>(options));
    }
}

@injectable()
export class CustomersApi {
    private api: CustomerPortalCustomers;

    constructor(@inject('baseUrl') @optional() baseUrl?: string) {
        this.api = new CustomerPortalCustomers(
            createMainAppMicroserviceInstance('/v1/customerportal2/customers', '/api/cp/customers'),
            baseUrl
        );
    }

    getServiceAddresses(cancelToken?: CancelToken): Promise<CustomerLocationsModel[]> {
        return this.api.getServiceAddresses(cancelToken).then(({ data }) => data);
    }
}
